import { isLocaleHomePath, stripLocalePrefix } from '@/components/portal/common/utils/localePath'
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import {
  findMenuNodeByPath,
  findRootMenuNode
} from '@/components/portal/common/utils/portalRouteResolver'
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import {
  FALLBACK_FAVICON_URL,
  FALLBACK_SITE_TITLE
} from '@/store/modules/webmaster'

/** @deprecated 请优先从 store getters['webmaster/siteTitle'] 读取 */
export const SITE_TITLE = FALLBACK_SITE_TITLE

const PORTAL_ROUTE_NAMES = new Set(['Portal', 'ProductDetail', 'ProductCatalogBackup'])
const DEFAULT_DESCRIPTION = '中光 — 防雷与电涌保护领域的专业企业'

export function isPortalRoute(route) {
  return PORTAL_ROUTE_NAMES.has(route?.name)
}

function resolveSiteTitle(siteTitle) {
  const title = String(siteTitle || '').trim()
  return title || FALLBACK_SITE_TITLE
}

/** 根据路径与菜单树解析完整浏览器标题 */
export function resolveDocumentTitle(
  path,
  menus = [],
  routeMeta = {},
  routeName = '',
  siteTitle = FALLBACK_SITE_TITLE
) {
  const baseTitle = resolveSiteTitle(siteTitle)
  const normalizedPath = stripLocalePrefix(path || HOME_PATH)

  if (isLocaleHomePath(path) || normalizedPath === HOME_PATH || routeMeta?.isHome) {
    return baseTitle
  }

  if (menus?.length) {
    const matched = findMenuNodeByPath(menus, normalizedPath)
    const rootMenu = matched ? findRootMenuNode(menus, matched) || matched : null
    if (rootMenu?.name) {
      return `${rootMenu.name} - ${baseTitle}`
    }
  }

  const metaTitle = routeMeta?.title
  if (metaTitle && metaTitle !== '首页') {
    return `${metaTitle} - ${baseTitle}`
  }

  // 门户页在菜单尚未加载时不要降级为纯站名
  if (isPortalRoute({ name: routeName })) {
    return null
  }

  return baseTitle
}

function ensureMetaTag(name) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  return el
}

function ensureIconLink() {
  let el =
    document.querySelector('link[rel="icon"]') ||
    document.querySelector('link[rel="shortcut icon"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'icon')
    document.head.appendChild(el)
  }
  return el
}

/** 同步 favicon / description / keywords */
export function applyDocumentHead({
  faviconUrl = '',
  description = '',
  keywords = ''
} = {}) {
  if (typeof document === 'undefined') return

  const icon = ensureIconLink()
  const resolvedFavicon = resolveFileUrl(faviconUrl) || FALLBACK_FAVICON_URL
  if (icon.getAttribute('href') !== resolvedFavicon) {
    icon.setAttribute('href', resolvedFavicon)
  }

  const descMeta = ensureMetaTag('description')
  descMeta.setAttribute('content', description || DEFAULT_DESCRIPTION)

  if (keywords) {
    ensureMetaTag('keywords').setAttribute('content', keywords)
  }
}

export function applyRouteDocumentTitle(route, menus = [], siteTitle = FALLBACK_SITE_TITLE) {
  if (!route) return false

  const title = resolveDocumentTitle(
    route.path,
    menus,
    route.meta || {},
    route.name,
    siteTitle
  )

  if (!title) return false

  document.title = title
  return true
}

function readWebmasterHead(store) {
  const getters = store.getters || {}
  return {
    siteTitle: getters['webmaster/siteTitle'] || FALLBACK_SITE_TITLE,
    faviconUrl: getters['webmaster/faviconUrl'] || FALLBACK_FAVICON_URL,
    description: getters['webmaster/description'] || '',
    keywords: getters['webmaster/keywords'] || ''
  }
}

/** 路由与菜单 / 站长配置变化时同步浏览器标签与 head */
export function setupDocumentTitleSync(router, store) {
  const refreshHead = () => {
    const { siteTitle, faviconUrl, description, keywords } = readWebmasterHead(store)
    applyDocumentHead({ faviconUrl, description, keywords })
    applyRouteDocumentTitle(router.currentRoute.value, store.state.nav.rawMenus, siteTitle)
  }

  store.subscribe((mutation) => {
    if (
      mutation.type === 'nav/SET_MENUS' ||
      mutation.type === 'locale/SET_CURRENT' ||
      mutation.type === 'webmaster/SET_WEBMASTER'
    ) {
      refreshHead()
    }
  })

  router.afterEach((to) => {
    const { siteTitle, faviconUrl, description, keywords } = readWebmasterHead(store)
    applyDocumentHead({ faviconUrl, description, keywords })
    const applied = applyRouteDocumentTitle(to, store.state.nav.rawMenus, siteTitle)

    const locale = store.state.locale.current
    if (!applied && isPortalRoute(to) && locale) {
      store.dispatch('nav/ensureRawMenus').then((menus) => {
        if (router.currentRoute.value.path !== to.path) return
        applyRouteDocumentTitle(to, menus, readWebmasterHead(store).siteTitle)
      }).catch(() => {})
    }
  })
}
